<script setup lang="ts">
import type { PropType, VNode } from 'vue'
import { AnimatePresence, motion, useReducedMotion } from 'motion-v'
import { Comment, computed, defineComponent, Fragment, h } from 'vue'

const props = withDefaults(defineProps<{ collapse?: boolean; x?: number; y?: number }>(), {
  collapse: false,
  x: 0,
  y: 0,
})
const slots = defineSlots<{ default?: () => VNode[] }>()
const reducedMotion = useReducedMotion()

// AnimatePresence needs the keyed rows, rather than v-for/conditional fragments.
function rows(nodes: VNode[] = slots.default?.() ?? []): VNode[] {
  return nodes.flatMap(node =>
    node.type === Fragment ? rows(node.children as VNode[]) : node.type === Comment ? [] : [node],
  )
}

const hidden = computed(() => ({
  opacity: 0,
  x: reducedMotion.value ? 0 : props.x,
  y: reducedMotion.value ? 0 : props.y,
  ...(props.collapse ? { height: 0, overflow: 'hidden' } : {}),
}))
const visible = computed(() => ({
  opacity: 1,
  x: 0,
  y: 0,
  ...(props.collapse ? { height: 'auto', transitionEnd: { overflow: 'visible' } } : {}),
}))
const transition = computed(() => ({
  type: 'tween' as const,
  duration: reducedMotion.value ? 0 : 0.24,
  ease: 'easeOut' as const,
}))

// Pass resolved nodes as a prop. Forwarding rows() through stable nested slots
// can retain a previous v-for alias (e.g. the active tasks for a day).
const RenderRows = defineComponent({
  props: { nodes: { type: Array as PropType<VNode[]>, required: true } },
  setup: rendered => () =>
    h(motion.div, { layoutScroll: true }, () =>
      h(
        AnimatePresence,
        {
          initial: false,
          mode: props.collapse ? 'sync' : 'popLayout',
        },
        () =>
          rendered.nodes.map((node, index) =>
            h(
              motion.div,
              {
                key: node.key ?? index,
                asChild: !props.collapse,
                layout: reducedMotion.value ? false : 'position',
                initial: hidden.value,
                animate: visible.value,
                exit: hidden.value,
                transition: transition.value,
              },
              () => node,
            ),
          ),
      ),
    ),
})
</script>

<template lang="pug">
RenderRows(:nodes="rows()")
</template>
